import React, { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
    getOrganization,
    createOrganization,
    updateOrganization
} from "../../api/overview";

// 초기 템플릿 데이터 (생성 시 사용)
const TEMPLATE_DATA = {
    id: "root",
    type: "dept", // 'dept' | 'person'
    name: "프로젝트 총괄",
    position: "", // dept 타입일 때 미사용 가능
    phone: "",
    company: "",
    children: [
        {
            id: "t1",
            type: "person",
            name: "현장소장",
            position: "소장",
            phone: "010-0000-0000",
            company: "메인건설",
            children: [],
        },
    ],
};

// 트리 평탄화 함수 (비상연락망용) - 부서(dept) 제외
const flattenTree = (node, list = [], parentDept = "") => {
    if (!node) return list;

    // 현재 노드가 부서라면, 자식들에게 물려줄 부서명은 현재 노드의 이름
    // 현재 노드가 사람이라면(보통 말단이지만), 부모 부서명을 그대로 전달 (단, 사람이 하위 조직을 가질 일은 드뭄)
    let nextParentDept = parentDept;
    if (node.type === 'dept') {
        nextParentDept = node.name;
    }

    // type이 person인 경우에만 리스트에 추가 (상속 부서 정보 포함)
    if (node.type === 'person') {
        list.push({ ...node, inheritedDept: parentDept });
    }

    if (node.children) {
        node.children.forEach((child) => flattenTree(child, list, nextParentDept));
    }
    return list;
};

// 노드 업데이트 재귀 함수
const updateNode = (node, id, field, value) => {
    if (node.id === id) {
        return { ...node, [field]: value };
    }
    if (node.children) {
        return {
            ...node,
            children: node.children.map((child) => updateNode(child, id, field, value)),
        };
    }
    return node;
};

// 자식 추가 재귀 함수
const addChildNode = (node, parentId, newChild) => {
    if (node.id === parentId) {
        return {
            ...node,
            children: [...(node.children || []), newChild]
        };
    }
    if (node.children) {
        return {
            ...node,
            children: node.children.map(child => addChildNode(child, parentId, newChild))
        };
    }
    return node;
}

// 노드 삭제 재귀 함수 (부모가 자식을 필터링하게 함)
const deleteNodeFromTree = (node, targetId) => {
    if (node.id === targetId) return null;

    if (node.children) {
        return {
            ...node,
            children: node.children
                .filter(child => child.id !== targetId)
                .map(child => deleteNodeFromTree(child, targetId))
                .filter(child => child !== null)
        };
    }
    return node;
}


// 재귀적 트리 노드 컴포넌트
const OrgNode = ({ node, isEdit, onUpdate, onAdd, onDelete, onNodeClick, parentDept }) => {
    const handleChange = (e) => {
        const { name, value } = e.target;
        onUpdate(node.id, name, value);
    };

    const nodeType = node.type || 'person';
    // 부서 노드면 자신의 이름을 자식에게 물려줄 부서명으로 사용, 아니면 상위에서 받은거 그대로 전달
    const nextParentDept = nodeType === 'dept' ? node.name : parentDept;

    // 화면에 표시할 소속: 입력된 company가 있으면 우선, 없으면 상위 부서명
    const displayAffiliation = node.company || parentDept || "-";

    return (
        <div className="flex flex-col items-center">
            {/* 노드 카드 */}
            <div
                className={`relative transition-all duration-300 z-10 group
                    ${nodeType === 'dept'
                        ? "bg-slate-700 text-white rounded-lg shadow-md hover:bg-slate-800"
                        : "bg-white text-gray-800 rounded-xl shadow-md border border-slate-200 hover:shadow-xl hover:border-blue-300"
                    }
                    ${isEdit
                        ? "p-4 border-2 border-dashed border-blue-400"
                        : "cursor-pointer hover:-translate-y-1"
                    }
                    ${nodeType === 'dept' ? 'min-w-[140px] px-4 py-3' : 'w-48'}
                `}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isEdit) onNodeClick({ ...node, inheritedDept: parentDept }); // 상세보기를 위해 상속 부서 정보 전달
                }}
            >
                {/* 편집 모드: 추가/삭제 버튼 */}
                {isEdit && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex space-x-1 z-20 whitespace-nowrap">
                        <div className="flex bg-white rounded-md shadow-sm border border-slate-200 overflow-hidden">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onAdd(node.id, 'dept');
                                }}
                                title="하위 부서 추가"
                                className="px-2 py-1 bg-slate-50 text-slate-600 text-[10px] font-bold hover:bg-slate-100 border-r border-slate-200 transition-colors"
                            >
                                +부서
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onAdd(node.id, 'person');
                                }}
                                title="하위 인원 추가"
                                className="px-2 py-1 bg-blue-50 text-blue-600 text-[10px] font-bold hover:bg-blue-100 transition-colors"
                            >
                                +인원
                            </button>
                        </div>

                        {node.id !== "root" && node.id !== TEMPLATE_DATA.id && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (window.confirm(`'${node.name}' 및 하위 조직을 모두 삭제하시겠습니까?`)) {
                                        onDelete(node.id);
                                    }
                                }}
                                className="bg-red-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs shadow hover:bg-red-600 transition-colors ml-1"
                            >
                                ✕
                            </button>
                        )}
                    </div>
                )}

                {/* 편집 모드: 입력 폼 */}
                {isEdit ? (
                    <div className="flex flex-col space-y-2 mt-1">
                        {nodeType === 'dept' ? (
                            <input
                                type="text"
                                name="name"
                                value={node.name}
                                onChange={handleChange}
                                className="text-sm font-bold text-center border-b border-white/30 rounded px-1 w-full bg-transparent text-white placeholder-white/50 focus:outline-none focus:border-white"
                                placeholder="부서명"
                                onClick={(e) => e.stopPropagation()}
                            />
                        ) : (
                            <>
                                <input
                                    type="text"
                                    name="position"
                                    value={node.position}
                                    onChange={handleChange}
                                    className="text-xs text-center border border-gray-200 rounded px-2 py-1 w-full bg-slate-50 focus:bg-white focus:border-blue-400 focus:outline-none transition-colors"
                                    placeholder="직책 (예: 공사팀장)"
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <input
                                    type="text"
                                    name="name"
                                    value={node.name}
                                    onChange={handleChange}
                                    className="text-sm font-bold text-center border border-gray-200 rounded px-2 py-1 w-full focus:border-blue-400 focus:outline-none transition-colors"
                                    placeholder="성명"
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <div className="flex gap-1">
                                    <input
                                        type="text"
                                        name="company"
                                        value={node.company}
                                        onChange={handleChange}
                                        className="text-[10px] text-center border border-gray-200 rounded px-1 py-1 w-1/2 bg-slate-50 focus:bg-white focus:border-blue-400 focus:outline-none transition-colors"
                                        placeholder={`소속 (${parentDept || '미지정'})`}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <input
                                        type="text"
                                        name="phone"
                                        value={node.phone}
                                        onChange={handleChange}
                                        className="text-[10px] text-center border border-gray-200 rounded px-1 py-1 w-1/2 text-slate-500 focus:text-slate-800 focus:border-blue-400 focus:outline-none transition-colors"
                                        placeholder="연락처"
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                ) : (
                    // 조회 모드: 뷰
                    <>
                        {nodeType === 'dept' ? (
                            <div className="flex items-center justify-center min-h-[40px]">
                                <span className="text-sm font-bold tracking-wide">{node.name}</span>
                            </div>
                        ) : (
                            <div className="flex flex-col h-full">
                                {/* 상단: 직책 + 소속 */}
                                <div className="bg-slate-50 border-b border-slate-100 rounded-t-xl px-3 py-2 text-center">
                                    <div className="text-xs font-semibold text-slate-600">{node.position || "-"}</div>
                                    <div className="text-[10px] text-slate-400 mt-0.5 truncat">{displayAffiliation}</div>
                                </div>
                                {/* 본문: 이름 */}
                                <div className="flex-1 flex items-center justify-center py-3 px-2">
                                    <div className="text-base font-bold text-gray-800">{node.name}</div>
                                </div>
                                {/* 하단: 전화번호 (있을때만) */}
                                {node.phone && (
                                    <div className="border-t border-slate-100 py-1.5 px-3 bg-white rounded-b-xl">
                                        <div className="text-[10px] text-center text-slate-400 font-mono">
                                            {node.phone}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* 자식이 있을 경우 하위 트리 렌더링 */}
            {node.children && node.children.length > 0 && (
                <div className="flex flex-col items-center">
                    {/* 부모와 자식 그룹을 잇는 수직선 */}
                    <div className="h-6 w-px bg-slate-300"></div>

                    {/* 자식들 가로 배치 */}
                    <div className="flex relative">
                        {node.children.map((child, index) => (
                            <div key={child.id} className="flex flex-col items-center px-3 md:px-6 relative">

                                {/* 수직선 (부모 연결선과 만나는 지점) */}
                                <div className="h-6 w-px bg-slate-300"></div>

                                {/* 가로선 로직 */}
                                {node.children.length > 1 && (
                                    <>
                                        <div className={`absolute top-0 left-0 w-1/2 h-px bg-slate-300 ${index === 0 ? "hidden" : "block"}`}></div>
                                        <div className={`absolute top-0 right-0 w-1/2 h-px bg-slate-300 ${index === node.children.length - 1 ? "hidden" : "block"}`}></div>
                                    </>
                                )}

                                <OrgNode
                                    node={child}
                                    isEdit={isEdit}
                                    onUpdate={onUpdate}
                                    onAdd={onAdd}
                                    onDelete={onDelete}
                                    onNodeClick={onNodeClick}
                                    parentDept={nextParentDept}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const OrganizationChart = () => {
    const { id: effectiveProjectId } = useParams();

    const [data, setData] = useState(null); // 초기값 null (데이터 없음 상태)
    const [orgId, setOrgId] = useState(null); // 서버 DB상의 Organization ID
    const [activeTab, setActiveTab] = useState("org"); // 'org' | 'emergency'
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedNode, setSelectedNode] = useState(null);

    // 컴포넌트 마운트 시 데이터 조회
    useEffect(() => {
        if (effectiveProjectId) {
            fetchOrganizationData();
        }
    }, [effectiveProjectId]);

    const fetchOrganizationData = async () => {
        try {
            // api/overview.js의 getOrganization 사용 (res.data 반환됨)
            const result = await getOrganization(effectiveProjectId);
            // 백엔드가 리스트를 반환한다고 가정 (ModelViewSet list)
            if (Array.isArray(result.results) && result.results.length > 0) {
                const orgData = result.results[0];
                setOrgId(orgData.id);
                if (orgData.tree) {
                    setData(orgData.tree);
                }
            } else {
                setData(null);
            }
        } catch (error) {
            console.error("Failed to fetch organization chart:", error);
            setData(null);
        }
    };
    // 트리 데이터가 변경될 때마다 비상연락망 리스트 자동 계산
    const contactList = useMemo(() => flattenTree(data), [data]);

    const handleUpdate = (id, field, value) => {
        setData((prev) => updateNode(prev, id, field, value));
    };

    const handleAddChild = (parentId, type) => {
        const newId = `node-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
        // 타입에 따른 기본 데이터
        const newChild = type === 'dept' ? {
            id: newId,
            type: 'dept',
            name: "새 부서",
            children: []
        } : {
            id: newId,
            type: 'person',
            name: "새 담당자",
            position: "직책",
            phone: "",
            company: "",
            children: []
        };

        setData(prev => addChildNode(prev, parentId, newChild));
    };

    const handleDeleteNode = (nodeId) => {
        // Root Node protection logic
        if (nodeId === TEMPLATE_DATA.id || nodeId === 'root') {
            alert('최상위 조직은 삭제할 수 없습니다.');
            return;
        }
        setData(prev => deleteNodeFromTree(prev, nodeId));
    };

    const handleNodeClick = (node) => {
        setSelectedNode(node);
    };

    const handleSave = async () => {
        if (!window.confirm("저장하시겠습니까?")) return;
        try {
            const payload = { tree: data };
            if (orgId) {
                // 수정: PUT /project/:id/organization/:orgId/
                await updateOrganization(effectiveProjectId, orgId, payload);
            } else {
                // 생성: POST /project/:id/organization/
                const result = await createOrganization(effectiveProjectId, payload);
                setOrgId(result.id);
            }
            alert("저장되었습니다.");
            setIsEditMode(false);
        } catch (error) {
            console.error("Failed to save organization chart:", error);
            alert("저장 중 오류가 발생했습니다.");
        }
    };

    // 데이터가 없는 경우 (초기 상태)
    if (!data) {
        return (
            <div className="p-8 flex flex-col items-center justify-center min-h-[400px] border border-slate-200 rounded-lg bg-slate-50">
                <p className="text-slate-500 mb-2 font-medium">현재 등록된 조직도가 없습니다.</p>
                <p className="text-slate-400 text-xs mb-6">프로젝트의 현장 조직도를 만들어 체계적으로 관리하세요.</p>
                <button
                    onClick={() => {
                        setData(TEMPLATE_DATA);
                        setIsEditMode(true);
                    }}
                    className="px-6 py-2.5 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition-all font-medium flex items-center gap-2 hover:shadow-lg hover:-translate-y-0.5"
                >
                    <span>✨</span> 조직도 생성하기
                </button>
            </div>
        );
    }

    return (
        <div className="p-6 pt-0 relative animate-in fade-in duration-500">
            {/* 등록/수정 모드 토글 (우측 상단) */}
            <div className="flex justify-end mb-4">
                <button
                    onClick={() => {
                        if (isEditMode) {
                            handleSave();
                        } else {
                            setIsEditMode(true);
                        }
                    }}
                    className={`px-4 py-2 rounded-lg shadow-sm text-sm font-medium transition-all flex items-center gap-2 ${isEditMode
                        ? "bg-blue-600 text-white hover:bg-blue-700 hover:shadow-md"
                        : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300"
                        }`}
                >
                    {isEditMode ? (
                        <>저장 완료</>
                    ) : (
                        <>조직도 등록/수정</>
                    )}
                </button>
            </div>

            <div className="mb-6">
                <div className="flex border-b border-gray-200">
                    <button
                        onClick={() => setActiveTab("org")}
                        className={`px-6 py-3 text-sm font-medium transition-colors relative ${activeTab === "org"
                            ? "text-blue-600"
                            : "text-gray-500 hover:text-gray-700"
                            }`}
                    >
                        현장 조직도
                        {activeTab === "org" && (
                            <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 rounded-t-full"></div>
                        )}
                    </button>
                    <button
                        onClick={() => setActiveTab("emergency")}
                        className={`px-6 py-3 text-sm font-medium transition-colors relative ${activeTab === "emergency"
                            ? "text-blue-600"
                            : "text-gray-500 hover:text-gray-700"
                            }`}
                    >
                        비상 연락망
                        {activeTab === "emergency" && (
                            <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 rounded-t-full"></div>
                        )}
                    </button>
                </div>
            </div>

            <div className={`bg-slate-50/50 rounded-xl border border-slate-200 min-h-[500px] overflow-auto relative scrollbar-hide
                ${activeTab === "org" ? "p-8" : "p-0"}
            `}>
                {activeTab === "org" && (
                    <div className="min-w-max pb-16 pt-4">
                        <div className="flex justify-center transform scale-95 origin-top">
                            <OrgNode
                                node={data}
                                isEdit={isEditMode}
                                onUpdate={handleUpdate}
                                onAdd={handleAddChild}
                                onDelete={handleDeleteNode}
                                onNodeClick={handleNodeClick}
                            />
                        </div>
                        <div className="text-center mt-16 flex flex-col items-center gap-2">
                            {isEditMode ? (
                                <div className="bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-xs font-medium border border-blue-100 shadow-sm max-w-md">
                                    💡 팁: [+부서] 또는 [+인원] 버튼을 눌러 하위 조직을 추가하세요.
                                </div>
                            ) : (
                                <p className="text-slate-400 text-xs">
                                    * 카드를 클릭하면 상세 정보를 확인할 수 있습니다.
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === "emergency" && (
                    <div className="w-full bg-white min-h-[500px]">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left text-gray-500">
                                <thead className="text-xs text-gray-700 uppercase bg-gray-50 border-b border-gray-100">
                                    <tr>
                                        <th className="px-6 py-4 text-center w-32">직책</th>
                                        <th className="px-6 py-4 text-center w-32">성명</th>
                                        <th className="px-6 py-4 text-center w-40">소속</th>
                                        <th className="px-6 py-4 text-center">연락처</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {contactList.map((person) => (
                                        <tr
                                            key={person.id}
                                            className={`hover:bg-blue-50/50 transition-colors ${selectedNode?.id === person.id ? "bg-blue-50" : ""}`}
                                        >
                                            <td className="px-6 py-4 text-center font-medium text-slate-600">{person.position || "-"}</td>
                                            <td className="px-6 py-4 text-center font-bold text-gray-900">{person.name}</td>
                                            <td className="px-6 py-4 text-center text-slate-500 text-xs">{person.company || person.inheritedDept || "-"}</td>
                                            <td className="px-6 py-4 text-center text-slate-500 font-mono text-xs">{person.phone || "-"}</td>
                                        </tr>
                                    ))}
                                    {contactList.length === 0 && (
                                        <tr>
                                            <td colSpan="4" className="text-center py-20">
                                                <div className="flex flex-col items-center justify-center text-slate-300">
                                                    <span className="text-4xl mb-2">📭</span>
                                                    <span>등록된 비상연락망 인원이 없습니다.</span>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* 선택된 노드 상세 팝업 (조직도 탭에서만 표시) */}
                {selectedNode && activeTab === "org" && !isEditMode && (
                    <div
                        className="fixed top-24 left-1/2 -ml-[500px] bg-white border border-slate-200 shadow-2xl rounded-xl p-6 w-80 z-[100] animate-in slide-in-from-left-4 duration-300 ring-1 ring-slate-900/10"
                        style={{ left: "calc(50% - 300px)", top: "120px" }} // 화면 중앙 기준, 약간 왼쪽 상단
                    >
                        <div className="flex justify-between items-start mb-4 border-b border-slate-100 pb-3">
                            <div>
                                <h4 className="font-bold text-2xl text-slate-800 tracking-tight">{selectedNode.name}</h4>
                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase mt-2 inline-block ${selectedNode.type === 'dept' ? 'bg-slate-100 text-slate-500' : 'bg-blue-50 text-blue-600'}`}>
                                    {selectedNode.type === 'dept' ? 'Department' : 'Person'}
                                </span>
                            </div>
                            <button
                                onClick={() => setSelectedNode(null)}
                                className="text-slate-300 hover:text-slate-500 p-2 hover:bg-slate-50 rounded-full transition-colors -mr-2 -mt-2"
                            >
                                ✕
                            </button>
                        </div>

                        {selectedNode.type === 'dept' ? (
                            <div className="text-sm text-slate-500 p-4 bg-slate-50 rounded-lg text-center">
                                하위 조직을 포함하는 부서 노드입니다.
                            </div>
                        ) : (
                            <div className="space-y-4 text-sm text-slate-600">
                                <div className="flex items-center justify-between">
                                    <span className="text-slate-400 text-xs font-semibold">직책</span>
                                    <span className="font-medium text-slate-800 text-base">{selectedNode.position || "-"}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-slate-400 text-xs font-semibold">소속</span>
                                    {/* 상속받은 부서명 또는 입력된 회사명 */}
                                    <span className="font-medium text-slate-700">{selectedNode.company || selectedNode.inheritedDept || "-"}</span>
                                </div>
                                <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 flex flex-col gap-1">
                                    <span className="text-slate-400 text-[10px] font-bold uppercase tracking-wider">Mobile</span>
                                    <a href={`tel:${selectedNode.phone}`} className="font-bold text-lg text-blue-600 font-mono tracking-tight hover:underline">
                                        {selectedNode.phone || "-"}
                                    </a>
                                </div>
                            </div>
                        )}

                        {selectedNode.type !== 'dept' && (
                            <div className="mt-6 pt-4 border-t border-slate-100 flex justify-end">
                                <button
                                    onClick={() => {
                                        setActiveTab("emergency");
                                    }}
                                    className="text-xs text-slate-500 hover:text-blue-600 flex items-center gap-1 transition-colors font-medium"
                                >
                                    비상연락망에서 확인하기 ➜
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default OrganizationChart;
